import { groupBySection, packColumns, type MenuBlock } from '../paginate.js';
import { featuredItem } from './cafe-panels.js';
import {
  bilingual,
  description,
  hasTitle,
  image,
  listColumns,
  listCss,
  listItem,
  priceBlock,
  sectionSubtitle,
  sectionTitle,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Half the screen is the featured dish, full bleed with its name and price over the photo; the
 * other half is a calm, well-spaced list. Works for any cuisine.
 */

const S = '.mb-t-split-hero';

function block(ctx: RenderContext, b: MenuBlock): string {
  const head = hasTitle(b.section)
    ? `<div class="sh-head">${sectionTitle(ctx, b.section, 'sh-title')}${b.continued ? '' : sectionSubtitle(ctx, b.section, 'sh-sub')}</div>`
    : '';
  return `<div class="sh-sec">${head}${b.items.map((i) => listItem(ctx, i, 'sh')).join('')}</div>`;
}

export const splitHero: MenuTemplate = {
  id: 'split-hero',
  name: 'Split hero',
  description: 'Half the screen is your featured dish, full height; the other half a clean, roomy list. Works for anything.',
  defaultTheme: {
    backgroundColor: '#fbfaf7',
    surfaceColor: '#ffffff',
    textColor: '#2a2a2a',
    primaryColor: '#111111',
    accentColor: '#e76f51',
    fontId: 'modern',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'light', name: 'Light', theme: {} },
    { id: 'dark', name: 'Dark', theme: { backgroundColor: '#121212', surfaceColor: '#1c1c1c', textColor: '#ececec', primaryColor: '#ffffff', accentColor: '#e9c46a' } },
    { id: 'terracotta', name: 'Terracotta', theme: { backgroundColor: '#f3e4d4', textColor: '#3b2a22', primaryColor: '#2d1f18', accentColor: '#a44a3f', fontId: 'classic' } },
    { id: 'forest', name: 'Forest', theme: { backgroundColor: '#0f2a1f', surfaceColor: '#163728', textColor: '#eef5ef', primaryColor: '#ffffff', accentColor: '#c8e66e', fontId: 'cafe' } },
  ],
  cuisines: ['pasta', 'cafe'],
  uses: { featured: true, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    const featured = featuredItem(board);
    return packColumns(
      groupBySection(board, featured?.id),
      listColumns(board, {
        count: 2,
        capacity: portrait ? (featured ? 56 : 100) : 51,
        heading: 3.8,
        item: { nameEm: 1.3, descEm: 0.85, perLine: portrait ? 40 : 32, gap: 1.4 },
      }),
      { balance: true },
    );
  },

  render(ctx) {
    const { board } = ctx;
    const item = featuredItem(board);
    const hero = item
      ? [
          '<div class="sh-hero">',
          image(ctx, item.imageMediaId, 'sh-hero-img'),
          '<div class="sh-hero-text">',
          bilingual(ctx, item.title, item.titleAr, 'sh-hero-name'),
          description(ctx, item, 'sh-hero-desc'),
          `<div class="sh-hero-price">${priceBlock(ctx, item, 'sh')}</div>`,
          '</div></div>',
        ].join('')
      : '';
    const header = [
      '<div class="sh-header">',
      bilingual(ctx, board.brand.name, board.brand.nameAr, 'sh-brand'),
      bilingual(ctx, board.settings.title ?? board.name, board.settings.titleAr, 'sh-heading'),
      '</div>',
    ].join('');
    const lists = `<div class="sh-cols">${ctx.page.columns
      .map((c) => `<div class="sh-col">${c.map((b) => block(ctx, b)).join('')}</div>`)
      .join('')}</div>`;
    return `<div class="sh-split${hero ? '' : ' sh-no-hero'}">${hero}<div class="sh-main">${header}${lists}</div></div>`;
  },

  css: `
${S}{padding:0}
${S} .sh-split{flex:1;min-height:0;display:grid;grid-template-columns:1fr 1fr}
${S}.mb-portrait .sh-split{grid-template-columns:1fr;grid-template-rows:42% 1fr}
${S} .sh-no-hero{grid-template-columns:1fr}
${S} .sh-hero{position:relative;min-height:0;overflow:hidden}
${S} .sh-hero-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
${S} .sh-hero-text{position:absolute;left:0;right:0;bottom:0;padding:8em 3em 2.8em;color:#fff;
  background:linear-gradient(transparent,rgba(0,0,0,.78))}
${S} .sh-hero-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3.6em;line-height:1.02}
${S} .sh-hero-name.mb-2nd{font-size:2.2em}
${S} .sh-hero-desc{font-size:1.15em;opacity:.9;margin-top:.4em;max-width:30em}
${S} .sh-hero-price{margin-top:.6em;font-size:1.6em}
${S} .sh-hero-price .sh-price{color:var(--mb-accent);font-weight:800}
${S} .sh-main{min-width:0;min-height:0;display:flex;flex-direction:column;padding:3.2em 3.4em 2.6em}
${S} .sh-header{margin-bottom:2em}
${S} .sh-brand{font-size:1em;letter-spacing:.35em;text-transform:uppercase;font-weight:700;color:var(--mb-accent)}
${S} .sh-heading{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3.4em;color:var(--mb-primary);line-height:1.05}
${S} .sh-heading.mb-2nd{font-size:2.1em}
${S} .sh-cols{flex:1;min-height:0;display:grid;grid-template-columns:1fr 1fr;gap:3em}
${S} .sh-col{min-width:0;display:flex;flex-direction:column;gap:2em}
${S} .sh-head{margin-bottom:1em}
${S} .sh-title{font-size:1em;letter-spacing:.3em;text-transform:uppercase;font-weight:800;color:var(--mb-accent)}
${S} .sh-title.mb-2nd{letter-spacing:0;font-size:1.1em}
${S} .sh-sub{font-size:.9em;opacity:.65;margin-top:.2em}
${listCss(S, 'sh', { name: '1.3em', price: '1.3em', desc: '.85em', leader: '.1em solid color-mix(in srgb,var(--mb-text) 18%,transparent)' })}
${S} .sh-item{margin-bottom:1.2em}
${S} .sh-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);color:var(--mb-primary)}
${S} .sh-price{font-weight:800;color:var(--mb-primary)}
`,
};
